import { Breadcrumb, Button, MenuProps } from 'antd';
import './PageScaffold.css';
import { Link } from 'react-router-dom';
import PageScaffoldControls, {
  type PageScaffoldFilterSettings,
} from './page_scaffold/PageScaffoldControls';
import { useContext, useEffect } from 'react';
import UIContext from '../contexts/ui/UIContext';
import PageScaffoldContext from '../contexts/ui/PageScaffoldContext';
import { useWindowSize } from '@leedpulse-monorepo/hooks';
import { useNavigate } from 'react-router-dom';

interface PageScaffoldProps {
  title: string;
  subtitle?: string;
  breadcrumbItems?: {
    target?: string;
    title: React.ReactNode;
  }[];
  controlsSettings?: {
    countryAddonBefore?: boolean;
    searchLabel: string;
    searchDefaultValue?: string;
    onSearch: (input: string, addonBeforeQuery: string) => void;
    onSearchCancel: () => void;
    hideSearch?: boolean;
    isLoading: boolean;
    actionButtons: {
      label: string | React.ReactNode;
      type?: 'primary' | 'default' | 'dashed' | 'link' | 'text';
      props?: React.ComponentProps<typeof Button>;
      buttonType?: string;
      items?: MenuProps['items'] | undefined;
      render?: () => React.ReactNode;
    }[];
  };
  filterSettings?: PageScaffoldFilterSettings;
  filters?: React.ReactNode;
}

const PageScaffold: React.FC<React.PropsWithChildren<PageScaffoldProps>> = ({
  children,
  title,
  subtitle,
  breadcrumbItems,
  controlsSettings,
  filterSettings,
  filters
}) => {
  const uiContext = useContext(UIContext);
  const pageScaffoldContext = useContext(PageScaffoldContext);
  const contentRef = pageScaffoldContext?.contentRef;
  const navigate = useNavigate();
  const windowSize = useWindowSize();

  useEffect(() => {
    uiContext.setPageTitle(title);
  }, [title]);

  useEffect(() => {
    if (contentRef?.current && windowSize.width >= 768 && windowSize.width < 1024) {
      contentRef.current.style.padding = '24px';
    } else if (contentRef?.current && windowSize.width < 768 && windowSize.width > 0) {
      contentRef.current.style.padding = '16px';
    }
  }, [windowSize.width]);

  // We parse the breadcrumb items and add use onClick to navigate to the target
  const breadcrumbItemsWithNavigation = breadcrumbItems?.map((item) => {
    if (!item.target) return item;
    return {
      ...item,
      onClick: () => {
        if (item.target) {
          navigate(item.target);
        }
      },
    };
  });

  return (
    <div className="page-scaffold">
      <div ref={contentRef} className="page-scaffold__content">
        <div className="page-scaffold__page-head">
          {breadcrumbItems && (
            <div className="page-scaffold__page-head__breadcrumb">
              <Breadcrumb
                separator=">"
                items={breadcrumbItemsWithNavigation}
                itemRender={(currentRoute) => {
                  if (currentRoute.onClick) {
                    const originalTarget =
                      breadcrumbItems?.find(
                        (item) => item.title === currentRoute.title
                      )?.target || '';
                    return (
                      <Link to={originalTarget}>{currentRoute.title}</Link>
                    );
                  }
                  return currentRoute.title;
                }}
              />
            </div>
          )}
          <h1>{title}</h1>
          {subtitle && <h3>{subtitle}</h3>}
        </div>

        {controlsSettings && (
          <PageScaffoldControls
            countryAddonBefore={controlsSettings.countryAddonBefore}
            searchLabel={controlsSettings.searchLabel}
            searchDefaultValue={controlsSettings.searchDefaultValue}
            onSearch={controlsSettings.onSearch}
            hideSearch={controlsSettings.hideSearch}
            isLoading={controlsSettings.isLoading}
            onSearchCancel={controlsSettings.onSearchCancel}
            actionButtons={controlsSettings.actionButtons}
            filterSettings={filterSettings}
          />
        )}
        {children}
      </div>
    </div>
  );
};

export default PageScaffold;
